import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { getReferrals, createReferral, updateReferral, deleteReferral } from '../services/api';
import { formatDateShort } from '../utils/dateUtils';
import { Plus, Share2, X, Loader2, Trash2, Edit, Users, AlertTriangle, ChevronDown } from 'lucide-react';

function Referrals() {
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const queryClient = useQueryClient();

  const { data: referrals, isLoading } = useQuery({
    queryKey: ['referrals'],
    queryFn: () => getReferrals().then(res => res.data),
  });

  const createMut = useMutation({
    mutationFn: createReferral,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referrals'] });
      closeModal();
      toast.success('Referral created');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateReferral(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referrals'] });
      closeModal();
      toast.success('Referral updated');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteReferral,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referrals'] });
      setDeleteItem(null);
      toast.success('Referral deleted');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Cannot delete'),
  });

  const openCreate = () => { setEditItem(null); setFormName(''); setShowModal(true); };
  const openEdit = (item) => { setEditItem(item); setFormName(item.name); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditItem(null); setFormName(''); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formName.trim()) { toast.error('Please enter a name'); return; }
    if (editItem) {
      updateMut.mutate({ id: editItem.id, data: { name: formName.trim() } });
    } else {
      createMut.mutate({ name: formName.trim() });
    }
  };

  const fmtDate = (d) => formatDateShort(d);

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="referrals-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Referrals</h1>
          <p className="text-slate-500 text-sm mt-1">Manage lead sources and track client referrals</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover" data-testid="add-referral-btn">
          <Plus size={18} /> Add Referral
        </button>
      </div>

      <div className="bg-white rounded-[10px] border overflow-hidden">
        <table className="w-full text-sm" data-testid="referrals-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left font-semibold text-slate-600">Name</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600">Added By</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600">Date Added</th>
              <th className="px-6 py-3 text-center font-semibold text-slate-600">Clients</th>
              <th className="px-6 py-3 text-right font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center"><Loader2 className="animate-spin mx-auto text-primary" size={28} /></td></tr>
            ) : !referrals?.length ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center">
                <Share2 size={48} className="mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">No referrals yet</p>
              </td></tr>
            ) : referrals.map((item) => (
              <React.Fragment key={item.id}>
                <tr className="hover:bg-orange-50/30 transition-colors" data-testid={`referral-row-${item.id}`}>
                  <td className="px-6 py-3.5 font-medium text-slate-800">{item.name}</td>
                  <td className="px-6 py-3.5 text-slate-600">{item.added_by}</td>
                  <td className="px-6 py-3.5 text-slate-600">{fmtDate(item.created_at)}</td>
                  <td className="px-6 py-3.5 text-center">
                    {item.client_count > 0 ? (
                      <button
                        onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors cursor-pointer"
                        data-testid={`expand-referral-${item.id}`}
                      >
                        <Users size={12} />{item.client_count}
                        <ChevronDown size={12} className={`transition-transform ${expandedId === item.id ? 'rotate-180' : ''}`} />
                      </button>
                    ) : (
                      <span className="text-slate-400 text-xs">0</span>
                    )}
                  </td>
                  <td className="px-6 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(item)} className="p-1.5 text-slate-500 hover:text-blue-600 rounded" title="Edit" data-testid={`edit-referral-${item.id}`}>
                        <Edit size={16} />
                      </button>
                      <button onClick={() => setDeleteItem(item)} className="p-1.5 text-slate-500 hover:text-red-500 rounded" title="Remove" data-testid={`delete-referral-${item.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === item.id && item.clients?.length > 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-0">
                      <div className="bg-slate-50 rounded-lg p-3 my-2 ml-4">
                        <p className="text-xs font-semibold text-slate-500 mb-2">Clients from {item.name}:</p>
                        <div className="flex flex-wrap gap-2">
                          {item.clients.map((c) => (
                            <Link
                              key={c.id}
                              to={`/clients/${c.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-700 hover:bg-orange-50 hover:border-[#f4931d] hover:text-[#f4931d] transition-colors"
                              data-testid={`referral-client-${c.id}`}
                            >
                              <Users size={11} />
                              {c.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[10px] w-full max-w-md shadow-xl" data-testid="referral-modal">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">{editItem ? 'Edit Referral' : 'Add New Referral'}</h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Website, Word of Mouth, Real Estate"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#f4931d]/40 focus:border-[#f4931d]"
                  autoFocus
                  data-testid="referral-name-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={isPending} className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-hover flex items-center gap-2 disabled:opacity-50" data-testid="save-referral-btn">
                  {isPending && <Loader2 size={14} className="animate-spin" />}
                  {editItem ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[10px] w-full max-w-sm shadow-xl" data-testid="delete-confirm-modal">
            <div className="p-6 text-center">
              <AlertTriangle size={40} className="mx-auto text-amber-500 mb-3" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Remove Referral</h3>
              <p className="text-sm text-slate-500 mb-1">
                Are you sure you want to remove <strong>{deleteItem.name}</strong>?
              </p>
              {deleteItem.client_count > 0 && (
                <p className="text-xs text-red-500 mt-2">
                  This referral is used by {deleteItem.client_count} client(s) and cannot be deleted.
                </p>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setDeleteItem(null)} className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
              <button
                onClick={() => deleteMut.mutate(deleteItem.id)}
                disabled={deleteMut.isPending || deleteItem.client_count > 0}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-delete-btn"
              >
                {deleteMut.isPending && <Loader2 size={14} className="animate-spin" />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Referrals;
